import { useState } from 'react';
import styled from '@emotion/styled';
import {
  Button,
  SalesToolsWarning,
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
  useSingletonMobileMediaQuery,
  useSingletonTabletMediaQuery,
} from '@sunrun/experience-ui-components';
import { useFeatureFlagEnabled } from '@sunrun/sales-experience-shared/react-feature-flags';
import { isSunrunManaged } from '@sunrun/sales-experience-shared';
import { DashboardWidgetContainer } from 'components/atoms/DashboardWidgetContainer';
import { getUrls } from 'helpers/getUrlHelper';
import { Mixpanel } from 'providers/Mixpanel';
import {
  BLOCK_ROW_GAP_PX,
  DashboardLayout,
  DashboardLayoutItem,
} from 'components/molecules/DashboardLayout';
import { ActionNeededWidget } from 'components/organisms/ActionNeededWidget';
import { StreamsWidget } from 'components/organisms/StreamsWidget';
import { HelpfulLinks } from 'components/organisms/HelpfulLinks';
import { AskHelpWidget } from 'components/organisms/AskHelpWidget';
import { NewsFeed } from 'components/organisms/NewsFeedWidget';
import { AnnouncementsWidget } from 'components/organisms/AnnouncementsWidget';
import { PerformanceComingSoon } from 'components/organisms/PerformanceComingSoon';
import { PerformanceWidget } from 'components/organisms/PerformanceWidget';
import {
  useDashboardProfilePhoto,
  useRepProfileData,
} from 'hooks/usePerformanceData';
import { useUserProfile } from 'providers/UserProfile';
import { getIconForRankValue, RANK_MAP } from 'components/atoms/RankIcons';
import { RecruiterStars } from 'components/atoms/RecruiterStars';
import { NewDashboardMobileAppDownload } from 'components/molecules/NewDashboardMobileAppDownload';
import { getTallyMarkForCount } from 'components/atoms/TallyMarks';
import { InfoIconPopover } from 'components/atoms/InfoIconPopover';
import { shouldHideSalesToolsWarning } from 'helpers/listViewHelpers';
import { getNewDashboardOrgType } from 'helpers/repTypeHelper';
import { useWorkdayLogHoursModal } from 'hooks/useWorkdayLogHoursModal';
import { useRecentTimeClockEvent } from 'hooks/useRecentTimeClockEvent';
import { WorkHoursStatusPill } from 'components/molecules/WorkHoursStatusPill';
import { CA_STREET_SALES_SCHEDULER } from 'constants/workday';
import { FEATURE_FLAGS } from 'constants/FeatureFlags';
import {
  RunXLearningCard,
  getRunXLearningBlockRows,
} from 'components/organisms/TrainingsWidget';
import {
  ReferralCenterCard,
  ReferralCenterLayoutItem,
  getReferralCenterBlockRows,
} from 'components/organisms/ReferralCenterWidget';
import { WORKDAY_HOURS__CLICKED_DASHBOARD_CLOCK_WORK_HOURS_BUTTON } from 'constants/mixpanelEvents';
import { useReferralProspects } from 'hooks/useReferralProspects';
import { useTrainings } from 'hooks/useTrainings';

type ReferralLearningPairProps = {
  isMobile: boolean;
  isDesktop: boolean;
};

const ReferralLearningPair = ({
  isMobile,
  isDesktop,
}: ReferralLearningPairProps) => {
  const [isReferralExpanded, setIsReferralExpanded] = useState(false);
  const [isTrainingsExpanded, setIsTrainingsExpanded] = useState(false);
  const { trainings, isTrainingsLoading, trainingsError, trainingAlert } =
    useTrainings();
  const { referrals, isLoading, error } = useReferralProspects();

  const blockRows = Math.max(
    getReferralCenterBlockRows(
      referrals.length,
      isMobile,
      isReferralExpanded,
      isDesktop,
    ),
    getRunXLearningBlockRows(
      trainings.length,
      isMobile,
      isTrainingsExpanded,
      isDesktop,
    ),
  );

  return (
    <DashboardLayoutItem
      blockColumns={isMobile ? 1 : isDesktop ? 3 : 2}
      blockRows={blockRows}>
      <PairGrid $sideBySide={isDesktop}>
        <PairSlot $order={isDesktop ? 1 : 2}>
          <ReferralCenterCard
            referrals={referrals}
            isLoading={isLoading}
            error={error ?? undefined}
            isExpanded={isReferralExpanded}
            onToggleExpand={() => setIsReferralExpanded((value) => !value)}
          />
        </PairSlot>
        <PairSlot $order={isDesktop ? 2 : 1}>
          <RunXLearningCard
            isExpanded={isTrainingsExpanded}
            onToggleExpand={() => setIsTrainingsExpanded((value) => !value)}
            trainings={trainings}
            isTrainingsLoading={isTrainingsLoading}
            trainingsError={trainingsError}
            trainingAlert={trainingAlert}
          />
        </PairSlot>
      </PairGrid>
    </DashboardLayoutItem>
  );
};

const NewDashboard = () => {
  const { oneBaseUrl } = getUrls();
  const { userProfile, splatUserType } = useUserProfile();
  const newLeaderboardEnabled = useFeatureFlagEnabled(
    FEATURE_FLAGS.NEW_LEADERBOARD_ENABLED,
  );
  const trainingsEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.RUN_X_TRAININGS);
  const workdayHoursWorkflowEnabled = useFeatureFlagEnabled(
    FEATURE_FLAGS.WORKDAY_HOURS_WORKFLOW,
  );
  const { openModal: openWorkdayLogHoursModal, isStatusRefreshing } =
    useWorkdayLogHoursModal();

  const isMobile = useSingletonMobileMediaQuery();
  const isTablet = useSingletonTabletMediaQuery();
  const isDesktop = !isMobile && !isTablet;

  const { profileData } = useRepProfileData();
  const previousRankName = profileData?.RunXStatus?.PreviousRank;
  const previousStatusRank: number =
    previousRankName != null && previousRankName in RANK_MAP
      ? RANK_MAP[previousRankName as keyof typeof RANK_MAP]
      : 0;
  const recruiterRank = profileData?.RecruiterStatus?.PreviousRank || '0 Star';
  const { profilePhotoUrl } = useDashboardProfilePhoto();
  const IMAGE_SIZE = 60;
  const sellsSunrun = userProfile?.sellsSunrun === 'Yes';

  const findStarCount = (rank: string) => {
    if (!rank) return 0;
    const m = rank.match(/(\d+)/);
    if (!m) return 0;
    return parseInt(m[1], 10);
  };

  const showLogWorkHours = Boolean(
    workdayHoursWorkflowEnabled &&
      userProfile?.title === CA_STREET_SALES_SCHEDULER,
  );
  const { recentEventStatus, recentEventType, recentTimeEntryCodeDescriptor } =
    useRecentTimeClockEvent(showLogWorkHours);
  const orgType = getNewDashboardOrgType(userProfile);
  const showUserGuidesFirst = orgType === 'AIR';
  const isStreetOrBlock = orgType === 'STREET' || orgType === 'BLOCK';
  const showScorecard = isStreetOrBlock || orgType === 'CHAIN';

  const isSRM = userProfile ? isSunrunManaged(userProfile) : false;
  const showTrainings = isSRM && trainingsEnabled;
  const userGuidesWidget = (
    <DashboardLayoutItem
      blockColumns={1}
      blockRows={showUserGuidesFirst && !isTablet ? 7 : 6}>
      <AskHelpWidget />
    </DashboardLayoutItem>
  );

  return (
    <Container>
      <HeaderSection>
        <SalesToolsWarning
          sellsSunrun={shouldHideSalesToolsWarning(
            userProfile,
            splatUserType ?? '',
          )}
        />
        <HeaderContainer>
          {profilePhotoUrl ? (
            <StyledHeaderImage
              src={profilePhotoUrl}
              alt="Dashboard Profile"
              width={IMAGE_SIZE}
              height={IMAGE_SIZE}
            />
          ) : (
            <SvgIcon
              name={SvgNames.Account}
              height={IMAGE_SIZE}
              width={IMAGE_SIZE}
            />
          )}
          <HeaderTitleBlock>
            <HeaderNameContainer>
              <Typography variant="h4">{userProfile?.firstName}</Typography>
              <LastNameHeader variant="h2" fontWeight="bold">
                {userProfile?.lastName}
              </LastNameHeader>
            </HeaderNameContainer>
            {isStreetOrBlock && profileData && (
              <div>
                <FlexDiv>
                  {getIconForRankValue({
                    rank: previousStatusRank,
                    sizePx: 40,
                    opacity: previousStatusRank === 0 ? 0.5 : 1,
                    orgType,
                  })}
                  {getTallyMarkForCount({
                    count: previousStatusRank,
                    sizePx: 40,
                    opacity: previousStatusRank === 0 ? 0.5 : 1,
                  })}
                  <InfoIconPopover
                    content="Your RunX Status and Recruiting Stars from last quarter"
                    contentAriaLabel="Your RunX Status and Recruiting Stars from last quarter"
                    triggerAriaLabel="Your RunX Status and Recruiting Stars from last quarter"
                    iconSize={16}
                  />
                </FlexDiv>
                <RecruiterStars filledCount={findStarCount(recruiterRank)} />
              </div>
            )}
          </HeaderTitleBlock>
          {showLogWorkHours && (
            <LogWorkHoursActions $isMobile={isMobile}>
              {!isMobile && (
                <WorkHoursStatusPill
                  status={recentEventStatus}
                  eventType={recentEventType}
                  timeEntryCodeDescriptor={recentTimeEntryCodeDescriptor}
                  isRefreshing={isStatusRefreshing}
                />
              )}
              <ClockWorkHoursButton
                color="primary"
                size="xs"
                onClick={() => {
                  openWorkdayLogHoursModal();
                  Mixpanel.track(
                    WORKDAY_HOURS__CLICKED_DASHBOARD_CLOCK_WORK_HOURS_BUTTON,
                    {
                      employee_id: userProfile?.employeeId,
                      first_name: userProfile?.firstName,
                      last_name: userProfile?.lastName,
                    },
                  );
                }}
                iconLeft={
                  <SvgIcon
                    name={SvgNames.ClockCountdown}
                    height={18}
                    width={18}
                    color={tokens.WHITE}
                  />
                }>
                Log your hours
              </ClockWorkHoursButton>
              {isMobile && (
                <WorkHoursStatusPill
                  status={recentEventStatus}
                  eventType={recentEventType}
                  timeEntryCodeDescriptor={recentTimeEntryCodeDescriptor}
                  isRefreshing={isStatusRefreshing}
                  isMobile
                />
              )}
            </LogWorkHoursActions>
          )}
        </HeaderContainer>
      </HeaderSection>
      <DashboardLayout>
        {showUserGuidesFirst && userGuidesWidget}
        {showScorecard && (
          <DashboardLayoutItem blockColumns={1} blockRows={14}>
            <DashboardWidgetContainer
              header="Scorecard"
              navigateTo={
                newLeaderboardEnabled
                  ? () => {
                      window.parent.postMessage(
                        {
                          type: 'COMMAND:CHANGE_URL',
                          source: 'Dashboard',
                          payload: { url: `${oneBaseUrl}/performance` },
                        },
                        '*',
                      );
                    }
                  : undefined
              }>
              <PerformanceWidget orgType={orgType} />
            </DashboardWidgetContainer>
          </DashboardLayoutItem>
        )}
        <DashboardLayoutItem blockColumns={1} blockRows={7}>
          <DashboardWidgetContainer
            header="Announcements"
            navigateTo={() => {
              window.parent.postMessage(
                {
                  type: 'COMMAND:CHANGE_URL',
                  source: 'Dashboard',
                  payload: {
                    url: `${oneBaseUrl}/notifications?announcements=true`,
                  },
                },
                '*',
              );
            }}>
            <AnnouncementsWidget />
          </DashboardWidgetContainer>
        </DashboardLayoutItem>
        <DashboardLayoutItem blockColumns={1} blockRows={2}>
          <DashboardWidgetContainer header="Helpful Links">
            <HelpfulLinks />
          </DashboardWidgetContainer>
        </DashboardLayoutItem>
        <DashboardLayoutItem blockColumns={1} blockRows={isDesktop ? 7 : 5}>
          <DashboardWidgetContainer
            header="News"
            navigateTo={() => {
              window.open('https://current.sunrun.com/', '_blank');
            }}>
            <NewsFeed orgType={orgType} />
          </DashboardWidgetContainer>
        </DashboardLayoutItem>
        {!showScorecard && (
          <DashboardLayoutItem blockColumns={1} blockRows={isDesktop ? 7 : 5}>
            <DashboardWidgetContainer header="Performance">
              <PerformanceComingSoon orgType={orgType} />
            </DashboardWidgetContainer>
          </DashboardLayoutItem>
        )}
        {!showUserGuidesFirst && userGuidesWidget}
        <DashboardLayoutItem blockColumns={1} blockRows={7}>
          <DashboardWidgetContainer header="Stream">
            <StreamsWidget />
          </DashboardWidgetContainer>
        </DashboardLayoutItem>
        {sellsSunrun && (
          <DashboardLayoutItem blockColumns={1} blockRows={isTablet ? 4 : 3}>
            <DashboardWidgetContainer
              header="Action Needed"
              subheader="Past 7 days"
              navigateTo={() => {
                window.parent.postMessage(
                  {
                    type: 'COMMAND:CHANGE_URL',
                    source: 'Dashboard',
                    payload: {
                      url: `${oneBaseUrl}/pipeline?filterUrgent=true`,
                    },
                  },
                  '*',
                );
              }}>
              <ActionNeededWidget />
            </DashboardWidgetContainer>
          </DashboardLayoutItem>
        )}
        <DashboardLayoutItem
          blockColumns={1}
          blockRows={isDesktop ? 2 : showScorecard ? 3 : 2}>
          <DashboardWidgetContainer header="Download ONE mobile app">
            <NewDashboardMobileAppDownload />
          </DashboardWidgetContainer>
        </DashboardLayoutItem>
        {showTrainings ? (
          <ReferralLearningPair isMobile={isMobile} isDesktop={isDesktop} />
        ) : (
          <ReferralCenterLayoutItem isMobile={isMobile} />
        )}
      </DashboardLayout>
    </Container>
  );
};

const PairGrid = styled.div<{ $sideBySide: boolean }>`
  display: grid;
  gap: ${BLOCK_ROW_GAP_PX}px;
  height: 100%;
  min-height: 0;
  grid-template-columns: 1fr;

  ${({ $sideBySide }) =>
    $sideBySide &&
    `
    grid-template-columns: 1fr 1fr;
  `}
`;

const PairSlot = styled.div<{ $order: number }>`
  min-width: 0;
  min-height: 0;
  height: 100%;
  order: ${({ $order }) => $order};
  display: flex;
  flex-direction: column;

  > * {
    flex: 1;
    min-height: 0;
  }
`;

const FlexDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const LastNameHeader = styled(Typography)`
  font-size: 32px !important;
  line-height: 32px !important;
`;

const StyledHeaderImage = styled.img<{ width: number; height: number }>`
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  object-fit: cover;
  object-position: center top;
  border-radius: 50%;
`;

const HeaderNameContainer = styled.div`
  & > * {
    line-height: initial !important;
  }
`;

const HeaderTitleBlock = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 25px;
  flex: 1;
  min-width: 0;
  flex-wrap: wrap;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const LogWorkHoursActions = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: ${({ $isMobile }) => ($isMobile ? 'column' : 'row')};
  align-items: ${({ $isMobile }) => ($isMobile ? 'stretch' : 'center')};
  gap: 16px;
  flex-shrink: 0;
`;

const ClockWorkHoursButton = styled(Button)`
  flex-shrink: 0;
`;

const Container = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export { NewDashboard };
